import type { SupabaseClient } from '@supabase/supabase-js';
import { Project } from '../types/project';

// MAINTAINER NOTE: This function is the single source of truth for what gets
// written to the Supabase projects table. When a new migration adds a column,
// add it here in the same commit. Never spread the project object into a
// Supabase write anywhere in the codebase.
export function toSupabasePayload(project: Project): Record<string, unknown> {
  return {
    // Project identification
    project_name: project.project_name ?? '',
    status: project.status ?? 'draft',

    // Customer info
    customer_name: project.customer_name ?? '',
    customer_address: project.customer_address ?? '',
    customer_city: project.customer_city ?? '',
    customer_state: project.customer_state ?? '',
    customer_zip: project.customer_zip ?? '',
    customer_email: project.customer_email ?? '',
    customer_phone: project.customer_phone ?? '',

    // Preparer / company info
    prepared_by: project.prepared_by ?? '',
    preparer_title: project.preparer_title ?? '',
    preparer_license: project.preparer_license ?? '',
    preparer_email: project.preparer_email ?? '',
    company_name: project.company_name ?? '',
    company_address: project.company_address ?? '',
    company_city: project.company_city ?? '',
    company_state: project.company_state ?? '',
    company_zip: project.company_zip ?? '',

    // AHJ / permit
    ahj_jurisdiction: project.ahj_jurisdiction ?? '',
    permit_number: project.permit_number ?? '',
    report_date: project.report_date ?? null,
    methodology_version: project.methodology_version ?? 'NEC 220.87 v1.0',

    // Existing electrical service
    service_voltage_text: project.service_voltage_text ?? '120/240V single-phase',
    service_voltage_numeric: project.service_voltage_numeric ?? 240,
    service_phase: project.service_phase ?? 'single',
    service_rating_amps: project.service_rating_amps,
    panel_bus_rating_amps: project.panel_bus_rating_amps ?? null,
    service_type: project.service_type ?? 'unknown',
    installation_status: project.installation_status ?? 'existing',

    // Utility
    utility_id: project.utility_id ?? null,
    utility_account_number: project.utility_account_number ?? null,
    location_id: project.location_id ?? null,
    customer_id: project.customer_id ?? null,

    // AMI data / measured load
    ami_data_provider: project.ami_data_provider ?? '',
    ami_measurement_period: project.ami_measurement_period ?? '',
    ami_occupancy_status: project.ami_occupancy_status ?? '',
    ami_interval_minutes: project.ami_interval_minutes ?? null,
    ami_data_quality_notes: project.ami_data_quality_notes ?? '',
    ami_site_notes: project.ami_site_notes ?? '',
    max_recorded_demand_kw: project.max_recorded_demand_kw ?? null,
    peak_timestamp: project.peak_timestamp ?? null,
    ami_generation_column_detected: project.ami_generation_column_detected ?? false,
    gross_peak_kw: project.gross_peak_kw ?? null,
    ami_merge_conflicts_resolved: project.ami_merge_conflicts_resolved ?? null,
    ami_merge_duplicates_removed: project.ami_merge_duplicates_removed ?? null,
    ami_merge_strategy: project.ami_merge_strategy ?? null,
    // Suspect-peak exclusions and estimated runs — migration 20261021144920
    ami_exclusions: project.ami_exclusions ?? null,
    ami_estimated_runs: project.ami_estimated_runs ?? null,

    // Traditional calculation (NEC 220.83/220.82) — migration 20260831221148
    report_method: project.report_method ?? null,
    // Null until the preparer picks an edition, or the project is completed
    // (which freezes the resolved one). Resolving a default here was the
    // bug: autosave promotes a new project to the cloud within seconds, so
    // the state was usually still blank and the row got stamped '2023' — and
    // a stored value outranks the state everywhere downstream, so selecting
    // Massachusetts afterwards changed nothing. Migration 20260903 made the
    // column nullable; readers resolve null through defaultEditionForState.
    code_edition: project.code_edition ?? null,
    // Null until completion freezes it (calc/rulesVersion.ts). Never resolved
    // here, for the same reason code_edition is not: a stored value outranks
    // the current rules everywhere downstream.
    calc_rules_version: project.calc_rules_version ?? null,
    measured_new_load_factors: project.measured_new_load_factors ?? true,
    traditional_submethod: project.traditional_submethod ?? 'auto',
    dwelling_floor_area_sqft: project.dwelling_floor_area_sqft ?? null,
    small_appliance_circuit_count: project.small_appliance_circuit_count ?? 2,
    laundry_circuit_count: project.laundry_circuit_count ?? 1,
    evse_demand_policy: project.evse_demand_policy ?? 'pooled',
    inventory_attested: project.inventory_attested ?? false,
    occupancy_type: project.occupancy_type ?? null,
    noncoincident_election: project.noncoincident_election ?? false,
    apply_evse_floor: project.apply_evse_floor ?? true,
    override_tier_kva: project.override_tier_kva ?? null,
    override_tier_percent: project.override_tier_percent ?? null,
    override_remainder_percent: project.override_remainder_percent ?? null,
    override_evse_percent: project.override_evse_percent ?? null,
    added_floor_area_sqft: project.added_floor_area_sqft ?? null,

    // Notes
    assumptions_notes: project.assumptions_notes ?? '',
  };
}

// Dev-only startup check. Queries the live projects table schema and compares
// it against every key toSupabasePayload() would send. Logs a loud error if
// any key has no matching column so mismatches are caught before they reach
// production. Silently no-ops in production or if the RPC is unavailable.
export async function validatePayloadSchema(supabase: SupabaseClient): Promise<void> {
  if (import.meta.env.MODE !== 'development') return;

  const payloadKeys = Object.keys(toSupabasePayload({} as Project));

  const { data, error } = await supabase.rpc('get_project_column_names');

  if (error) {
    console.warn('[TrueLoad] Schema validation skipped (RPC unavailable):', error.message);
    return;
  }

  const dbColumns = new Set(
    (data as Array<{ column_name: string }>).map((r) => r.column_name)
  );
  const mismatched = payloadKeys.filter((k) => !dbColumns.has(k));

  if (mismatched.length > 0) {
    console.error(
      `[TrueLoad] Schema mismatch detected. The following fields are in toSupabasePayload() but have no column in the projects table: ${mismatched.join(', ')}. Add a migration before pushing.`
    );
  }
}
